import React from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { AppShell, type NavItem } from '@mbbe/ui';
import { useLanguage } from '../contexts/LanguageContext';
import { useCompanyProfile } from '../hooks/useSiteContent';
import { LEGAL_PAGE_DEFAULTS, LEGAL_PAGE_SLUGS } from '@mbbe/types';
import { openConsentPreferences } from '../lib/analyticsConsent';
import { publicNavigation } from '../lib/publicNavigation';

// Not a route: the footer renders this entry as a button that reopens the consent panel.
const COOKIE_PREFERENCES_ITEM_PATH = '#cookie-preferences';

/**
 * Layout wrapper that provides routing context to AppShell organism
 */
const Layout: React.FC = () => {
  const location = useLocation();
  const profile = useCompanyProfile();
  const { language, setLanguage, t } = useLanguage();

  const toggleLanguage = () => {
    setLanguage(language === 'tr' ? 'en' : 'tr');
  };

  const navigation = publicNavigation(language, t);
  const navItems: NavItem[] = navigation.primary;
  const footerNavItems: NavItem[] = [
    ...navigation.footer,
    ...(profile.isPending ? [] : LEGAL_PAGE_SLUGS.map((slug) => ({ name: profile.legalTitles?.[slug]?.[language] || LEGAL_PAGE_DEFAULTS[slug][language].title, path: `/${slug}` }))),
    { name: t('consent_preferences_link'), path: COOKIE_PREFERENCES_ITEM_PATH },
  ];

  return (
    <AppShell
      brandName="mbbe"
      navItems={navItems}
      footerNavItems={footerNavItems}
      currentPath={location.pathname}
      language={language}
      onLanguageToggle={toggleLanguage}
      ctaText={t('nav_quote')}
      ctaPath="/teklif-al"
      footerDescription={
        <>
          {t('footer_desc')}
          {profile.isPending ? <span role="status" aria-label={t('contact_title')} className="mt-6 block h-32 animate-pulse bg-white/5" /> : <span className="mt-6 block space-y-2 text-sm not-italic">
            <strong className="block text-slate-200">{profile.legalName}</strong>
            <span className="block">{profile.address}</span>
            {profile.email && <a className="block hover:text-white" href={`mailto:${profile.email}`}>{profile.email}</a>}
            {profile.phone && <a className="block hover:text-white" href={`tel:${profile.phone.replace(/[^+\d]/g, '')}`}>{profile.phone}</a>}
            {profile.taxOffice.trim() && <span className="block">{language === 'tr' ? 'Vergi dairesi' : 'Tax office'}: {profile.taxOffice}</span>}
            {profile.taxNumber.trim() && <span className="block">{language === 'tr' ? 'Vergi no' : 'Tax number'}: {profile.taxNumber}</span>}
          </span>}
        </>
      }
      quickLinksTitle={t('quick_links')}
      copyrightText={`© 2026 mbbe. ${t('rights_reserved')}`}
      designedInText={t('designed_in')}
      renderHomeLink={(children, className) => (
        <NavLink to="/" className={className}>
          {children}
        </NavLink>
      )}
      renderNavLink={(item, _isActive, className) => (
        <NavLink
          key={item.path}
          to={item.path}
          end={item.path === '/'}
          className={({ isActive }) =>
            className.replace(
              _isActive ? 'text-brand-500' : 'text-slate-300',
              isActive ? 'text-brand-500' : 'text-slate-300'
            )
          }
        >
          {item.name}
        </NavLink>
      )}
      renderCtaLink={(text, path, className, onClick) =>
        onClick ? (
          <button type="button" onClick={onClick} className={className}>
            {text}
          </button>
        ) : (
          <NavLink to={path} className={className}>
            {text}
          </NavLink>
        )
      }
      renderFooterLink={(item, className) =>
        item.path === COOKIE_PREFERENCES_ITEM_PATH ? (
          <button type="button" onClick={openConsentPreferences} className={className}>
            {item.name}
          </button>
        ) : (
          <NavLink to={item.path} className={className}>
            {item.name}
          </NavLink>
        )
      }
    >
      <Outlet />
    </AppShell>
  );
};

export default Layout;
